import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { ShieldCheck, AlertTriangle, FileText, Receipt } from "lucide-react";
import { supabase, isSupabaseConfigured } from "../../lib/supabase";
import { hashAuditShareToken } from "../../lib/org/auditShares";

// ---------------------------------------------------------------------------
// /audit-share/:token — public read-only view.
//
// We never render anything sensitive until the server-side
// fetch_audit_share_data RPC returns a valid payload. On any failure mode
// (unknown token, expired, revoked) we show a generic "unavailable" screen
// — no distinction, so the page doesn't leak whether the token was ever
// valid.
// ---------------------------------------------------------------------------

const E2E_BYPASS = import.meta.env.VITE_E2E_BYPASS_AUTH === "true";
const BYPASS_SHARES_KEY = "proofly.bypass.audit_shares.v1";
const BYPASS_EXPENSES_KEY = "proofly.expenses.v1";
const BYPASS_INVOICES_KEY = "proofly.invoices.v1";

interface PayloadExpense {
  id: string;
  vendor: string;
  occurred_on: string;
  amount_cents: number;
  currency: string;
  status: string;
  business_purpose: string | null;
}

interface PayloadInvoice {
  id: string;
  counterparty: string;
  direction: string;
  amount_cents: number;
  currency: string;
  issued_on: string;
  due_on: string | null;
  status: string;
}

interface AuditSharePayload {
  organization_id: string;
  expires_at: string;
  scope_json: unknown;
  expenses: PayloadExpense[];
  invoices: PayloadInvoice[];
}

interface BypassShareRow {
  id: string;
  name: string;
  expiresAt: string;
  revokedAt: string | null;
  url?: string;
  scope: {
    dateFrom?: string;
    dateTo?: string;
    types?: Array<"expenses" | "invoices">;
  };
}

function formatAmount(cents: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

async function resolveBypass(rawToken: string): Promise<AuditSharePayload | null> {
  if (typeof window === "undefined") return null;
  let rows: BypassShareRow[] = [];
  try {
    const raw = window.localStorage.getItem(BYPASS_SHARES_KEY);
    rows = raw ? (JSON.parse(raw) as BypassShareRow[]) : [];
  } catch { rows = []; }
  // In bypass we accept any non-empty token that matches by URL suffix
  // OR by direct id (so tests don't have to know the raw SHA-256). First,
  // try to find a row whose stored URL ends with the raw token; otherwise
  // return the most-recent non-revoked, non-expired row.
  const byUrl = rows.find((r) => r.url && r.url.endsWith(`/${rawToken}`));
  const pick = byUrl ?? rows.find((r) => !r.revokedAt && new Date(r.expiresAt).getTime() > Date.now());
  if (!pick || pick.revokedAt || new Date(pick.expiresAt).getTime() <= Date.now()) return null;
  // Build payload from localStorage expense/invoice data.
  let expenses: PayloadExpense[] = [];
  let invoices: PayloadInvoice[] = [];
  try {
    const expRaw = window.localStorage.getItem(BYPASS_EXPENSES_KEY);
    const expArr = expRaw ? (JSON.parse(expRaw) as Array<Record<string, unknown>>) : [];
    expenses = expArr.map((e) => ({
      id: String(e.id ?? ""),
      vendor: String(e.vendor ?? ""),
      occurred_on: String(e.occurredOn ?? ""),
      amount_cents: Number(e.amountCents ?? 0),
      currency: String(e.currency ?? "USD"),
      status: String(e.status ?? "draft"),
      business_purpose: e.businessPurpose ? String(e.businessPurpose) : null,
    }));
  } catch { expenses = []; }
  try {
    const invRaw = window.localStorage.getItem(BYPASS_INVOICES_KEY);
    const invArr = invRaw ? (JSON.parse(invRaw) as Array<Record<string, unknown>>) : [];
    invoices = invArr.map((i) => ({
      id: String(i.id ?? ""),
      counterparty: String(i.counterparty ?? ""),
      direction: String(i.direction ?? ""),
      amount_cents: Number(i.amountCents ?? 0),
      currency: String(i.currency ?? "USD"),
      issued_on: String(i.issuedOn ?? ""),
      due_on: i.dueOn ? String(i.dueOn) : null,
      status: String(i.status ?? "pending"),
    }));
  } catch { invoices = []; }

  // Filter by scope
  const types = pick.scope.types;
  if (types && !types.includes("expenses")) expenses = [];
  if (types && !types.includes("invoices")) invoices = [];
  if (pick.scope.dateFrom) {
    expenses = expenses.filter((e) => e.occurred_on >= pick.scope.dateFrom!);
    invoices = invoices.filter((i) => i.issued_on >= pick.scope.dateFrom!);
  }
  if (pick.scope.dateTo) {
    expenses = expenses.filter((e) => e.occurred_on <= pick.scope.dateTo!);
    invoices = invoices.filter((i) => i.issued_on <= pick.scope.dateTo!);
  }

  return {
    organization_id: "bypass",
    expires_at: pick.expiresAt,
    scope_json: pick.scope,
    expenses,
    invoices,
  };
}

export function AuditShareView() {
  const { token } = useParams<{ token: string }>();
  const [payload, setPayload] = useState<AuditSharePayload | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "unavailable">("loading");

  useEffect(() => {
    let cancelled = false;
    async function run(): Promise<void> {
      if (!token) { setStatus("unavailable"); return; }
      try {
        if (E2E_BYPASS) {
          const p = await resolveBypass(token);
          if (cancelled) return;
          if (!p) { setStatus("unavailable"); return; }
          setPayload(p);
          setStatus("ok");
          return;
        }
        if (!isSupabaseConfigured) { setStatus("unavailable"); return; }
        const tokenHash = await hashAuditShareToken(token);
        const { data, error } = await supabase.rpc("fetch_audit_share_data", { token_hash: tokenHash });
        if (cancelled) return;
        if (error || !data) { setStatus("unavailable"); return; }
        setPayload(data as AuditSharePayload);
        setStatus("ok");
      } catch {
        if (!cancelled) setStatus("unavailable");
      }
    }
    void run();
    return () => { cancelled = true; };
  }, [token]);

  const totalRows = useMemo(() => {
    if (!payload) return 0;
    return payload.expenses.length + payload.invoices.length;
  }, [payload]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div data-testid="audit-share-view" className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }

  if (status === "unavailable" || !payload) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div data-testid="audit-share-view" className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-8 text-center space-y-3">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
          <h1 className="text-xl font-semibold text-slate-900">Share unavailable</h1>
          <p className="text-sm text-slate-500">
            This share link is expired, revoked, or invalid. Contact the person who shared it with you.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div data-testid="audit-share-view" className="max-w-5xl mx-auto space-y-6">
        <header className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-[#0070F2]" />
            <h1 className="text-2xl font-semibold text-slate-900">Proofly audit view</h1>
          </div>
          <div className="text-xs text-slate-500">
            Share expires {formatDate(payload.expires_at)}
          </div>
        </header>

        {totalRows === 0 ? (
          <div data-testid="audit-share-empty" className="bg-white border border-slate-200 rounded-lg p-8 text-center text-sm text-slate-500">
            No records in the selected scope.
          </div>
        ) : (
          <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table data-testid="audit-share-table" className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Vendor / Counterparty</th>
                  <th className="px-4 py-2">Amount</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payload.expenses.map((e) => (
                  <tr key={`exp:${e.id}`} className="hover:bg-slate-50/60">
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                        <Receipt className="w-3.5 h-3.5" /> Expense
                      </span>
                    </td>
                    <td className="px-4 py-2 font-medium text-slate-900">{e.vendor}</td>
                    <td className="px-4 py-2 font-semibold text-slate-900">{formatAmount(e.amount_cents, e.currency)}</td>
                    <td className="px-4 py-2 text-slate-700">{formatDate(e.occurred_on)}</td>
                    <td className="px-4 py-2 text-slate-700 capitalize">{e.status}</td>
                  </tr>
                ))}
                {payload.invoices.map((i) => (
                  <tr key={`inv:${i.id}`} className="hover:bg-slate-50/60">
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                        <FileText className="w-3.5 h-3.5" /> Invoice
                      </span>
                    </td>
                    <td className="px-4 py-2 font-medium text-slate-900">{i.counterparty}</td>
                    <td className="px-4 py-2 font-semibold text-slate-900">{formatAmount(i.amount_cents, i.currency)}</td>
                    <td className="px-4 py-2 text-slate-700">{formatDate(i.issued_on)}</td>
                    <td className="px-4 py-2 text-slate-700 capitalize">{i.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </div>
  );
}
